import {
  Component,
  inject,
  OnInit,
  signal,
  DestroyRef,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common';
import { DeviceDetectorService } from 'ngx-device-detector';
import { MaterialModule } from '../../../shared/material/material.module';
import { FormBuilder, Validators, FormArray } from '@angular/forms';
import { UserPreferencesCacheService } from '../../../shared/services/user-preferences-cache.service';
import {
  ServiceOfferingControllerService,
  ServiceOffering,
  Council,
  CouncilProfession,
  CouncilControllerService,
  ProfileProviderPfControllerService,
  Address,
} from '../../../core/generated';
import { AvailabilityComponent } from '../../../shared/availability/availability.component';
import { firstValueFrom } from 'rxjs';
import { registerLocaleData } from '@angular/common';
import localePt from '@angular/common/locales/pt';
import { CurrencyMaskDirective } from '../../../shared/directives/currency-mask.directive';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { getEnumKey } from '../../../shared/utils/enum.util';
import { ContainerComponent } from '../../../shared/container/container.component';

// Register pt-BR locale for currency formatting
registerLocaleData(localePt);

@Component({
  selector: 'app-service-offering-provider-pf-form',
  standalone: true,
  imports: [
    CommonModule,
    MaterialModule,
    AvailabilityComponent,
    CurrencyMaskDirective,
    ContainerComponent
  ],
  templateUrl: './service-offering-provider-pf-form.component.html',
  styleUrls: ['./service-offering-provider-pf-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ServiceOfferingProviderPFFormComponent implements OnInit {
  private readonly serviceOfferingService = inject(ServiceOfferingControllerService);
  private readonly deviceService = inject(DeviceDetectorService);
  private readonly userPreferencesCacheService = inject(UserPreferencesCacheService);
  private readonly councilService = inject(CouncilControllerService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly fb = inject(FormBuilder);
  private readonly profileProviderPFService = inject(ProfileProviderPfControllerService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);

  serviceOfferingProviderPF: ServiceOffering[] = [];
  readonly isMobile = this.deviceService.isMobile();
  readonly panelOpenState = signal(false);
  councils: Council[] = [];
  profilePFId: string | null = null;
  id: string | null = null;
  addresses: Address[] = [];
  professionsOptions: CouncilProfession[] = [];
  isLoading = true;

  private currentUserCpf: string | null = null;

  formServiceOffering = this.fb.group({
    id: [''],
    profileProviderPfId: [''],
    professionName: ['', Validators.required],
    specialty: ['', Validators.required],
    activityAreas: this.fb.array([]),
    adultCare: [false],
    pediatricCare: [false],
    serviceFormatOnLine: [false],
    serviceFormatPresential: [false],
    address: [null as Address | null],
    availability: this.fb.array([]),
    publicationDate: [''],
    expirationPublicationDate: [''],
    amount: [null as number | null],
    billingUnit: ['HOUR'],
    description: [''],
    status: ['DRAFT' as ServiceOffering.StatusEnum],
    emergencyService: [false],
    emergencyServiceAvailability: this.fb.array([]),
  });

  async ngOnInit() {
    // Carregar CPF do usuário logado
    await this.loadCurrentUserCpf();

    const days = [21, 22, 23, 31, 32, 33, 41, 42, 43, 51, 52, 53, 61, 62, 63, 71, 72, 73, 81, 82, 83];

    const availabilityArray = this.formServiceOffering.get('availability') as FormArray;
    availabilityArray.clear();
    days.forEach((availability) => {
      availabilityArray.push(this.fb.control(availability));
    });

    const emergencyServiceAvailabilityArray = this.formServiceOffering.get(
      'emergencyServiceAvailability'
    ) as FormArray;
    emergencyServiceAvailabilityArray.clear();
    days.forEach((availability) => {
      emergencyServiceAvailabilityArray.push(this.fb.control(availability));
    });

    const routeSnapshot = this.route.snapshot;
    if (routeSnapshot.parent && routeSnapshot.parent.paramMap.has('id')) {
      this.profilePFId = routeSnapshot.parent.paramMap.get('id');
    }
    if (routeSnapshot.paramMap.has('id')) {
      this.id = routeSnapshot.paramMap.get('id');
    }
    if (this.profilePFId) {
      this.loadCouncils(this.profilePFId);
      await this.loadAddresses(this.profilePFId);
      if (this.id) {
        this.loadServiceOffering(this.id);
      }
    } else if (this.id) {
      this.loadServiceOffering(this.id);
    }
  }

  private async loadCurrentUserCpf(): Promise<void> {
    try {
      const user = await firstValueFrom(this.userPreferencesCacheService.getUser());
      this.currentUserCpf = (user as any)?.cpf || null;
    } catch (error) {
      console.error('Error loading user CPF:', error);
      this.currentUserCpf = null;
    }
  }

  async loadAddresses(profileId: string): Promise<void> {
    const profileProviderId = parseInt(profileId, 10);
    try {
      const addresses = await firstValueFrom(
        this.profileProviderPFService.getAllAddresses1(profileProviderId)
      );
      this.addresses = addresses || [];
      this.cdr.markForCheck();
    } catch (error) {
      console.error('Error loading addresses:', error);
      this.addresses = [];
    }
  }

  getIdFromRoute(): string | null {
    let routeSnapshot = this.route.snapshot;

    // Verificar na rota atual
    if (routeSnapshot.paramMap.has('id')) {
      return routeSnapshot.paramMap.get('id');
    }

    // Verificar na rota pai
    if (routeSnapshot.parent && routeSnapshot.parent.paramMap.has('id')) {
      return routeSnapshot.parent.paramMap.get('id');
    }

    // Verificar na rota avô
    if (
      routeSnapshot.parent &&
      routeSnapshot.parent.parent &&
      routeSnapshot.parent.parent.paramMap.has('id')
    ) {
      return routeSnapshot.parent.parent.paramMap.get('id');
    }

    // Se não encontrou nada, tenta extrair da URL atual
    const urlMatch = this.router.url.match(/\/providerPF\/([^\/]+)/);
    if (urlMatch) {
      return urlMatch[1];
    }

    return null;
  }

  loadServiceOffering(id: string) {
    this.serviceOfferingService
      .findById2(Number(id))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (serviceOffering: ServiceOffering) => {
          const serviceAddress = serviceOffering.address;
          const { address, ...serviceOfferingWithoutAddress } = serviceOffering;

          this.formServiceOffering.patchValue(serviceOfferingWithoutAddress as any);

          if (serviceAddress) {
            this.setAddressInForm(serviceAddress);
          }

          // Atualize manualmente o FormArray de availability
          const availabilityArray = this.formServiceOffering.get('availability') as FormArray;
          availabilityArray.clear();
          if (serviceOffering.availability) {
            serviceOffering.availability.forEach((availability: number) => {
              availabilityArray.push(this.fb.control(availability));
            });
          }

          // Atualize manualmente o FormArray de emergencyServiceAvailability
          const emergencyServiceAvailabilityArray = this.formServiceOffering.get(
            'emergencyServiceAvailability'
          ) as FormArray;
          emergencyServiceAvailabilityArray.clear();
          if (serviceOffering.emergencyServiceAvailability) {
            serviceOffering.emergencyServiceAvailability.forEach((availability: number) => {
              emergencyServiceAvailabilityArray.push(this.fb.control(availability));
            });
          }

          this.cdr.markForCheck();
        },
        error: (error: any) => {
          console.error('Error loading service offering:', error);
        }
      });
  }

  private setAddressInForm(serviceAddress: Address): void {
    const matchedAddress = this.addresses.find((addr) => addr.id === serviceAddress.id);
    if (matchedAddress) {
      this.formServiceOffering.get('address')?.setValue(matchedAddress);
    } else {
      this.addresses.push(serviceAddress);
      this.formServiceOffering.get('address')?.setValue(serviceAddress);
    }
    this.formServiceOffering.get('serviceFormatPresential')?.setValue(true);
  }

  async loadCouncils(id: string) {
    this.isLoading = true;
    const profileProviderPF = await firstValueFrom(
      this.profileProviderPFService.findById7(Number(id))
    );

    if (profileProviderPF && profileProviderPF.id) {
      this.councilService
        .findAllByProfileProviderPFId(profileProviderPF.id)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (councilList: Council[]) => {
            this.professionsOptions = [];
            councilList.forEach((council: Council) => {
              if (council.councilProfession) {
                council.councilProfession.forEach((councilProfession: CouncilProfession) => {
                  if (councilProfession.certifiedHubMedical) {
                    this.professionsOptions.push(councilProfession);
                  }
                });
              }
            });
            this.cdr.markForCheck();
          },
          complete: () => {
            this.isLoading = false;
            this.cdr.markForCheck();
          },
          error: (error: any) => {
            console.error('Error loading councils:', error);
            this.isLoading = false;
            this.cdr.markForCheck();
          }
        });
    } else {
      this.isLoading = false;
    }
  }

  addServiceOffering() {
    // Garantir que profilePFId não seja null ao salvar
    if (!this.profilePFId) {
      this.profilePFId = this.getIdFromRoute();
    }
    if (!this.profilePFId) {
      this.formServiceOffering.markAllAsTouched();
      return;
    }

    this.formServiceOffering.controls.profileProviderPfId.setValue(this.profilePFId);

    if (this.formServiceOffering.invalid) {
      this.formServiceOffering.markAllAsTouched();
      return;
    }

    const value = this.formServiceOffering.value;

    // Regra especial para status: se publicationDate estiver nulo/vazio, DRAFT, caso contrário OPEN
    const statusValue = (!value.publicationDate || value.publicationDate === '')
      ? ServiceOffering.StatusEnum.Draft
      : ServiceOffering.StatusEnum.Open;

    const serviceOffering: ServiceOffering = {
      id: value.id ? Number(value.id) : undefined,
      cnpj: this.currentUserCpf ?? '',
      professionName: value.professionName ?? '',
      specialty: value.specialty ?? '',
      activityAreas: this.preparTosaveActivityAreas(),
      adultCare: value.adultCare ?? false,
      pediatricCare: value.pediatricCare ?? false,
      serviceFormatOnLine: value.serviceFormatOnLine ?? false,
      serviceFormatPresential: value.serviceFormatPresential ?? false,
      address: value.address ?? undefined,
      availability: (value.availability as number[]) ?? [],
      publicationDate: value.publicationDate ?? '',
      expirationPublicationDate: value.expirationPublicationDate ?? '',
      amount: value.amount ?? undefined,
      billingUnit: value.billingUnit ?? 'HOUR',
      description: value.description ?? '',
      status: statusValue,
      emergencyService: value.emergencyService ?? false,
      emergencyServiceAvailability: (value.emergencyServiceAvailability as number[]) ?? [],
    };

    if (this.id) {
      this.serviceOfferingService
        .update1(Number(this.id), serviceOffering)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            this.router.navigate(['/providerPF', this.profilePFId, 'serviceOffering']);
          },
          error: (error: any) => {
            console.error('Error updating service offering:', error);
          }
        });
    } else {
      this.serviceOfferingService
        .save1(serviceOffering)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            this.router.navigate(['/providerPF', this.profilePFId, 'serviceOffering']);
          },
          error: (error: any) => {
            console.error('Error creating service offering:', error);
          }
        });
    }
  }

  get availability() {
    return this.formServiceOffering.get('availability') as FormArray;
  }

  get emergencyServiceAvailability() {
    return this.formServiceOffering.get('emergencyServiceAvailability') as FormArray;
  }

  onAvailabilityChange(newAvailability: FormArray) {
    this.formServiceOffering.setControl('availability', newAvailability);
  }

  onEmergencyAvailabilityChange(newAvailability: FormArray) {
    this.formServiceOffering.setControl('emergencyServiceAvailability', newAvailability);
  }

  get activityAreas() {
    return this.formServiceOffering.get('professionInfo.activityAreas') as any;
  }

  addActivityArea() {
    this.activityAreas.push(
      this.fb.group({
        activityArea: [''],
        activityAreaRQE: [''],
      })
    );
  }

  removeActivityArea(index: number) {
    this.activityAreas.removeAt(index);
  }

  dateFilter = (date: Date | null): boolean => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const result = date ? date >= today : false;
    return result;
  };

  dateLimitFilter = (date: Date | null): boolean => {
    let publishDate = new Date();
    let today = new Date();

    if (this.formServiceOffering.controls.publicationDate.value) {
      publishDate = new Date(this.formServiceOffering.controls.publicationDate.value);
    } else {
      this.formServiceOffering.controls.expirationPublicationDate.reset();
    }

    if (this.formServiceOffering.controls.publicationDate.value) {
      today = new Date(this.formServiceOffering.controls.publicationDate.value);
    }
    today.setHours(0, 0, 0, 0);
    const result = date ? date >= today : false;
    return result;
  };

  getProfessionName() {
    if (this.professionsOptions) {
      const tiposUnicos = [
        ...new Set(
          this.professionsOptions.map((item) => item.professionInfo!.professionName)
        ),
      ];
      return tiposUnicos;
    }
    return [];
  }

  getSpecialty() {
    if (this.formServiceOffering.controls['professionName'].value && this.professionsOptions) {
      const professionName = this.formServiceOffering.controls['professionName']?.value;
      if (professionName && this.professionsOptions) {
        const councilProfessionFiter1 = this.professionsOptions.filter(
          (councilProfession) =>
            councilProfession.professionInfo &&
            councilProfession.professionInfo.professionName === professionName
        );
        const tiposUnicos = [
          ...new Set(
            councilProfessionFiter1
              .map((item) => item.professionInfo?.specialty)
              .filter((s) => !!s)
          ),
        ];
        return tiposUnicos;
      }
    }
    return [];
  }

  getActivityArea() {
    if (this.formServiceOffering.controls['specialty'].value && this.professionsOptions) {
      const councilProfessionFiter1 = this.professionsOptions.filter(
        (councilProfession) =>
          councilProfession.professionInfo!.professionName ===
          this.formServiceOffering.controls['professionName']?.value &&
          councilProfession.professionInfo!.specialty ===
          this.formServiceOffering.controls['specialty']?.value
      );
      const tiposUnicos = [
        ...new Set(
          councilProfessionFiter1.map((item) => item.professionInfo!.activityAreas)
        ),
      ];
      return tiposUnicos[0];
    }
    return [];
  }

  preparTosaveActivityAreas(): string[] {
    let items = this.getActivityArea();
    if (items && items.length > 0) {
      return items
        .map((item) => item.activityArea)
        .filter((activityArea) => activityArea !== undefined) as string[];
    }
    return [];
  }
}
